import { useState } from "react";
import { Link } from "react-router-dom";
import { BrandLockup } from "@/components/layout/BrandLockup";
import { authService } from "@/lib/auth/authService";

type Status = "idle" | "loading" | "sent" | "error";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [serverError, setServerError] = useState("");

  function validate(): boolean {
    if (!email.trim()) {
      setEmailError("Email address is required.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("Please enter a valid email address.");
      return false;
    }
    return true;
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;

    setStatus("loading");

    const result = await authService.requestPasswordReset(email);

    if (result.success) {
      // Always show "sent" even if the email isn't registered — prevents
      // attackers from discovering which emails exist in the database.
      setStatus("sent");
    } else {
      setStatus("error");
      setServerError(result.error ?? "Something went wrong. Please try again.");
    }
  };

  const fieldClass = (hasError: boolean) =>
    `w-full px-4 py-3 rounded-lg transition-colors outline-none ${
      hasError ? "ring-2 ring-[rgba(185,28,28,0.4)]" : ""
    }`;

  const fieldStyle = (hasError: boolean): React.CSSProperties => ({
    backgroundColor: "#ffffff",
    color: "#10201d",
    border: hasError ? "1px solid #b91c1c" : "1px solid #bdc9c4",
  });

  const fieldFocusHandler = (hasError: boolean) => ({
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.style.borderColor = hasError ? "#b91c1c" : "#006b5f";
      e.target.style.boxShadow = hasError
        ? "0 0 0 2px rgba(185,28,28,0.4)"
        : "0 0 0 2px rgba(0,107,95,0.35)";
    },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.style.borderColor = hasError ? "#b91c1c" : "#bdc9c4";
      e.target.style.boxShadow = "none";
    },
  });

  // ── Success state ──────────────────────────────────────────────────────────
  if (status === "sent") {
    return (
      <div
        className="flex min-h-[100dvh] items-center justify-center overflow-y-auto px-4 py-8 sm:px-6"
        style={{ backgroundColor: "#f3f5f2" }}
      >
        <div className="max-w-md w-full text-center">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: "rgba(0, 107, 95, 0.1)" }}
          >
            <svg
              className="w-8 h-8"
              style={{ color: "#006b5f" }}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 9v.906a2.25 2.25 0 0 1-1.183 1.981l-6.478 3.488M2.25 9v.906a2.25 2.25 0 0 0 1.183 1.981l6.478 3.488m8.839 2.51-4.66-2.51m0 0-1.023-.55a2.25 2.25 0 0 0-2.134 0l-1.022.55m0 0-4.661 2.51m16.5 1.615a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V8.844a2.25 2.25 0 0 1 1.183-1.981l7.5-4.039a2.25 2.25 0 0 1 2.134 0l7.5 4.039a2.25 2.25 0 0 1 1.183 1.98V19.5Z"
              />
            </svg>
          </div>

          <h1
            className="text-3xl font-bold mb-3"
            style={{ color: "#10201d" }}
          >
            Check your email
          </h1>
          <p className="mb-2" style={{ color: "#52645f" }}>
            If{" "}
            <span style={{ color: "#10201d", fontWeight: 500 }}>{email}</span>{" "}
            is registered with Momentum Peaks, you'll receive a reset link
            shortly.
          </p>
          <p className="text-sm mb-8" style={{ color: "#8a9b95" }}>
            The link expires in 1 hour. Check your spam folder if you don't see
            it.
          </p>

          <Link
            to="/login"
            className="block w-full px-4 py-3 font-bold rounded-lg transition-all text-center"
            style={{ backgroundColor: "#006b5f", color: "#ffffff" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#00574e";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#006b5f";
            }}
          >
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  // ── Form state ─────────────────────────────────────────────────────────────
  return (
    <main
      id="main-content"
      className="flex min-h-[100dvh] overflow-x-hidden overflow-y-auto"
      style={{ backgroundColor: "#f3f5f2" }}
    >
      {/* Left Side - Form */}
      <div className="flex min-w-0 flex-1 items-center justify-center px-4 py-6 sm:px-6 sm:py-12">
        <div className="max-w-md w-full">
          <div className="mb-8 sm:mb-12">
            <Link
              to="/"
              className="inline-block hover:opacity-80 transition-opacity"
            >
              <BrandLockup size="xl" />
            </Link>
          </div>

          <div className="mb-8">
            <h1
              className="text-3xl sm:text-4xl font-bold mb-2"
              style={{ color: "#10201d" }}
            >
              Forgot password?
            </h1>
            <p style={{ color: "#52645f" }}>
              Enter your email and we'll send you a reset link.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium mb-2"
                style={{ color: "#344844" }}
              >
                Email Address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError("");
                }}
                className={fieldClass(!!emailError)}
                style={{
                  ...fieldStyle(!!emailError),
                  ...(status === "loading" ? { opacity: 0.6 } : {}),
                }}
                placeholder="you@example.com"
                disabled={status === "loading"}
                aria-invalid={!!emailError}
                aria-describedby={emailError ? "forgot-email-error" : undefined}
                {...fieldFocusHandler(!!emailError)}
              />
              {emailError && (
                <p
                  id="forgot-email-error"
                  role="alert"
                  className="mt-1.5 text-xs flex items-center gap-1"
                  style={{ color: "#b91c1c" }}
                >
                  <svg
                    className="w-3 h-3 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-9a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {emailError}
                </p>
              )}
            </div>

            {serverError && (
              <div
                role="alert"
                className="p-3.5 rounded-lg text-sm flex items-start gap-2"
                style={{
                  backgroundColor: "rgba(185, 28, 28, 0.06)",
                  border: "1px solid rgba(185, 28, 28, 0.15)",
                  color: "#b91c1c",
                }}
              >
                <svg
                  className="w-4 h-4 mt-0.5 shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10A8 8 0 1 1 2 10a8 8 0 0 1 16 0Zm-7 4a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm-1-9a1 1 0 0 0-1 1v4a1 1 0 1 0 2 0V6a1 1 0 0 0-1-1Z"
                    clipRule="evenodd"
                  />
                </svg>
                {serverError}
              </div>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              className="w-full px-4 py-3 font-bold rounded-lg transition-all"
              style={{
                backgroundColor: status === "loading" ? "#bdc9c4" : "#006b5f",
                color: status === "loading" ? "#8a9b95" : "#ffffff",
                cursor: status === "loading" ? "not-allowed" : "pointer",
              }}
              onMouseEnter={(e) => {
                if (status !== "loading")
                  e.currentTarget.style.backgroundColor = "#00574e";
              }}
              onMouseLeave={(e) => {
                if (status !== "loading")
                  e.currentTarget.style.backgroundColor = "#006b5f";
              }}
            >
              {status === "loading" ? "Sending..." : "Send Reset Link"}
            </button>
          </form>

          <div className="mt-8 text-center text-sm">
            <Link
              to="/login"
              className="font-medium hover:underline transition-opacity"
              style={{ color: "#006b5f" }}
            >
              ← Back to Sign In
            </Link>
          </div>
        </div>
      </div>

      {/* Right Side - Hero Image */}
      <div className="hidden lg:block flex-1 relative">
        <div
          className="absolute inset-0 z-10"
          style={{
            background:
              "linear-gradient(to bottom right, rgba(16,32,29,0.55), rgba(16,32,29,0.55))",
          }}
        ></div>
        <img
          src="https://images.unsplash.com/photo-1758957097813-9b391db18caa?w=1200&q=80"
          alt="Cyclist training"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-12 left-12 right-12 z-20">
          <h2
            className="text-3xl font-bold mb-2"
            style={{ color: "#ffffff" }}
          >
            Back on the Bike
          </h2>
          <p
            className="text-lg"
            style={{ color: "rgba(255,255,255,0.75)" }}
          >
            Reset your password and get back to training.
          </p>
        </div>
      </div>
    </main>
  );
}
